import hashed from 'lib/includes/hashed/main';
import {transform} from 'ol/proj';
import { getDefaultBaseLayerCodes, getVisibleBaseLayerCodes, getVisibleLayerCodes, syncLayers, requestWktFeature } from "lib/utilities/map";
import { setToolsMaster, setDrawToolsMaster } from "lib/utilities/toolmodes";
import { toPrecision } from "lib/utilities/maths";

function synchronize(map, mapConfig, options) {
  options = options || {};
  let animate;
  if ('animate' in options) {
    animate = options.animate;
  } else {
    animate = {duration: 250};
  }

  const view = map.getView();
  const projection = view.getProjection().getCode();

  let zoom, center, rotation;
  
  if (view.isDef()) {
    zoom = view.getZoom();
    center = view.getCenter();
    rotation = view.getRotation();
	
  } else {
    const viewport = map.getViewport();
    if (viewport) {
      zoom = Math.LOG2E * Math.log(viewport.clientWidth / 256);
    } else {
      zoom = 0;
    }
    center = [0, 0];
    rotation = 0;
  }

  const config = {
    center: {
      default: center,
      serialize: function(coord, state) {
        let precision;
        if (state && 'zoom' in state) {
          precision = Math.max(0, Math.ceil(Math.log(state.zoom) / Math.LN2));
        } else {
          precision = 3;
        }
        coord = transform(coord, projection, 'EPSG:4326');
        return (
          toPrecision(coord[1], precision) +
          ',' +
          toPrecision(coord[0], precision)
        );
      },
      deserialize: function(str) {
        const parts = str.split(',');
        if (parts.length !== 2) {
          throw new Error('Expected lon,lat but got ' + str);
        }
        const coord = [parseFloat(parts[1]), parseFloat(parts[0])];
        return transform(coord, 'EPSG:4326', projection);
      }
    },
    zoom: {
      default: zoom,
      serialize: function(zoom) {
        //return toPrecision(value, 0);
		return Math.round(zoom);
      },
      deserialize: Number
    },
    /*rotation: {
      default: rotation,
      serialize: function(value) {
        return toPrecision(value, 2);
      },
      deserialize: Number
    },*/
	baselayer: {
		default: getDefaultBaseLayerCodes(map),
		serialize: function(value) {
			return value.join(",");
		},
		deserialize: function(str) {
			return str.split(",");
		}
	},
	layers: {
		default: getVisibleLayerCodes(map),
		serialize: function(value) {
			return value.join(",");
		},
		deserialize: function(str) {
			return str.split(",");
		}
	},
	feature: {
		default: null,
		serialize: function(value) {
			return value; // int
		},
		deserialize: function(value) {
			if(!isNaN(value))
				return Number(value);
				
			return "";
		}
	},
	tools: {
		default: null,
		serialize: function(value) {
			return value?.join(",");
		},
		deserialize: function(value) {
			return value?.split(",");
		}
	},
	drawtools: {
		default: null,
		serialize: function(value) {
			return value?.join(",");
		},
		deserialize: function(value) {
			return value?.split(",");
		}
	},
	lang: {
		default: _config.languageId,
		serialize: function(value) {
			return String(value);
		},
		deserialize: function(value) {
			return value;
		}
	}
  };

  function hashHandler(state) {
	  _config.hashState = state;
	  
	if ('feature' in state) {
		requestWktFeature(state.feature, map);
	}
	if ('tools' in state) {
		setToolsMaster(state.tools);
	}
	if ('drawtools' in state) {
		setDrawToolsMaster(state.drawtools);
	}
	if ('layers' in state) {
		syncLayers(state.layers, map);
	}
	if ('baselayer' in state) {
		syncLayers([state.baselayer, ...getVisibleLayerCodes(map)], map);
	}
	if('center' in state || 'zoom' in state) {
		if(state.center != center || state.zoom != zoom) { // If default values assume no url info provided
			map.set("url-positioned", true);
		}
	}
	if ('lang' in state) {
		const locale = mapConfig.locales.find(l => l.code == state.lang);
		if(locale) {
			_config.setLangAndLocale(locale);
		}else{
			console.warn('Hashed locale update: Cant find locale by code', state.lang);
		}
	}
	
	if (view.isDef() && animate) {
		view.animate(Object.assign({}, state, animate));
		return;
	}
	if ('center' in state) {
		view.setCenter(state.center);
		map.set("url-positioned", true);
	}
	if ('zoom' in state) {
		view.setZoom(state.zoom);
	}
	/*if ('rotation' in state) {
		view.setRotation(state.rotation);
	}*/
  }

  const update = hashed.register(config, hashHandler);

  function onMoveEnd() {
    update({
      center: view.getCenter(),
      zoom: view.getZoom(),
      //rotation: view.getRotation()
    });
  }
  
  function onLayerUpdate() {
	const baseLayers = getVisibleBaseLayerCodes(map);
	const layers = getVisibleLayerCodes(map);

	update({
		baselayer: baseLayers,
		layers: layers
	});
	
	// Update the hash state layers so when layers are re-built, it knows what layers were visible before
	_config.hashState.layers = layers;
  }
  function onNewLayerGroups() {
		if(!_config.hashState) 
			return;
		
		syncLayers(_config.hashState.layers, map);
  }
  function onHashedUpdate(params) {
	  update(params);
  }

  map.on('moveend', onMoveEnd);
  
  _events.on("layer-toggled", onLayerUpdate);
  _events.on("layer-update", onLayerUpdate);
  _events.on("new-opus-layer-groups", onNewLayerGroups);
  _events.on("hashed-update", onHashedUpdate);
  
  // Get the param names of the sync objects
  const params = [];
  
  for(let param in config)
  	params.push(param);

  return {
		unregister: function unregister() {
			map.un('moveend', onMoveEnd);
			_events.un("layer-toggled", onLayerUpdate);
			_events.un("layer-update", onLayerUpdate);
			_events.un("new-opus-layer-groups", onNewLayerGroups);
			_events.un("hashed-update", onHashedUpdate);
			hashed.unregister(hashHandler);
		},
		instance: hashed,
		params: params
  };
}

export default synchronize;
